<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        size="lg"
        :padding="false"
        :stacked="stackedModal">
        <template v-slot:header>
            <div class="pt-6 px-6 flex items-center gap-2">
                <span>
                    {{ data.auth_config_id ? 'Edit' : 'New' }} API Auth
                </span>
                <experimental-pill type="beta"
                    class="-mt-1"></experimental-pill>
            </div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">This will {{ data.auth_config_id ? 'update' : 'create' }} and select a new API Auth.
            </div>
        </template>

        <page-loading v-if="loading"></page-loading>
        <form v-else
            @submit.prevent="handleSubmit()"
            autocomplete="off"
            id="auth-config-modal-form"
            class="flex flex-col gap-4 py-4 px-5 lg:py-7 lg:px-10">


            <div class="text-2xl font-extrabold w-full text-slate-800">
                Details</div>

            <auth-config-form-name ref="nameRef"></auth-config-form-name>

            <template v-if="data.auth_config_type === 'oauth2_client_credentials'">
                <div class="text-xl font-extrabold w-full text-slate-800 mt-4">OAuth Client Credentials
                </div>
                <auth-config-form-client-credentials></auth-config-form-client-credentials>
            </template>
            <template v-else>
                <div class="text-xl font-extrabold w-full text-slate-800">Custom Header</div>
                <auth-config-form-headers ref="headersStepRef"></auth-config-form-headers>
            </template>
        </form>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click="emit('update:visible', false)"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button type="submit"
                    form="auth-config-modal-form"
                    :loading="submitting"
                    class="grow"
                    size="lg"
                    color="primary">
                    {{ data.auth_config_id ? 'Update' : 'Create and Select' }} API Auth <font-awesome-icon widthAuto
                        :icon="faArrowRight"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import {
    VxModal,
    VxButton,
} from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import ExperimentalPill from "../../../../general/ExperimentalPill.vue";
import AuthConfigFormName from './AuthConfigFormName.vue';
import AuthConfigFormHeaders from './AuthConfigFormHeaders.vue';
import { useAuthConfigForm } from "./useAuthConfigForm";
import { nextTick, ref, watch, computed } from "vue";
import useVuelidate from "@vuelidate/core";
import PageLoading from "~/components/general/PageLoading.vue";
import AuthConfigFormClientCredentials from "~/components/settings/integrations/auth-configs/form/AuthConfigFormClientCredentials.vue";
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { validateRules } from '~/utils/validation';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    authConfigId: {
        type: String,
        required: false,
        default: undefined,
    },
    authConfigType: {
        type: String,
        required: false,
        default: 'custom_header',
    }
});

const emit = defineEmits(['update:visible', 'upserted']);

const nameRef = ref();
const headersStepRef = ref()

const stackedModal = computed(() => headersStepRef.value?.basicModal)

const { catchError } = useFormRequestHandler();
const { data, submit, submitting, loading } = useAuthConfigForm();

const validate = useVuelidate();
const handleSubmit = async () => {
    try {
        if (!await validateRules(validate)) {
            return;
        }
        emit('upserted', await submit());
        emit('update:visible', false)
    } catch (e) {
        catchError(e);
    }
}

watch(() => props.visible, async () => {
    if (props.visible) {
        data.value.auth_config_id = props.authConfigId;
    }
}, {
    immediate: true,
});

watch(() => props.authConfigType, async () => {
    await nextTick();
    data.value.auth_config_type = props.authConfigType;
}, {
    immediate: true,
});
</script>
